import { Injectable, Inject } from '@angular/core';
import { TchekOldService } from './tchek-old.service';
import { fabric } from 'fabric_gesture';
import { FavoritePictureFilters } from '../model/detail';
import { RoiService } from './roi.service';
import 'parse';
const parse: typeof Parse = require('parse');
import { ContractUpdate, HElementChecklist } from '../tchek/tchek-detail/contract-update';
import { ChecklistItem } from '../model/hitech';
import { LOCALE_ID } from '@angular/core';

declare const pdfMake: any;

// Adding custom table layout for white borders
pdfMake.tableLayouts = {
  whiteBorders: {
    hLineWidth: function (i, node) {
      return 4;
    },
    vLineWidth: function (i) {
      return 4;
    },
    hLineColor: function (i) {
      return 'white';
    },
    vLineColor: function (i) {
      return 'white';
    },
    paddingLeft: function (i) {
      return 0;
    },
    paddingRight: function (i, node) {
      return 0;
    }
  }
};

// Adding custom font Quicksand
pdfMake.fonts = {
  Quicksand: {
    normal: 'Quicksand-Regular.ttf',
    bold: 'Quicksand-Bold.ttf',
    italics: 'Quicksand-Medium.ttf',
    bolditalics: 'Quicksand-Medium.ttf'
  }
};

export enum PdfType {
  STATE,
  COMPARISON,
  UNIQUE,
  DAMAGE
}

@Injectable()
export class PdfService {
  private tchek: Parse.Object;
  private tchekRois: Array<Parse.Object>;
  private imagesOffset: number;
  private headerImage: any;
  private footerImage: any;
  private arrowDownImage: any;
  private security: any;
  private customer: any;
  private employee: any;
  private vehicle: any;
  private companyLogo: any;
  private companyMap: any;
  private contract: any;
  private fuelLevel: Array<any>;
  private stateData: ContractUpdate;
  private checklist: ChecklistItem[];

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private _tchekService: TchekOldService,
    private _roiService: RoiService) {
    this.imagesOffset = 0;
    this.tchekRois = [];
    this.fuelLevel = [];
    this.contract = {};
    this.checklist = [];
  }

  async buildPDF(tchekId: string, pdfData: any | ContractUpdate, type?: PdfType): Promise<any> {
    this.tchek = await this._tchekService.getTchek(tchekId);
    //this.contract = this.tchek.get('contractPtr');
    this.tchekRois = await this._roiService.getTchekRois(tchekId, true);
    // this.headerImage = await this.getDataUri('assets/images/header_merge.png', false);
    // this.footerImage = await this.getDataUri('assets/images/bar_blue_2px.png', false);
    this.arrowDownImage = (await this.getDataUri('assets/images/arrow_down.jpg', false))[0];
    this.fuelLevel['0'] = (await this.getDataUri('assets/icon/gauge_empty@2x.png', false))[0];
    this.fuelLevel['25'] = (await this.getDataUri('assets/icon/gauge_quarter@2x.png', false))[0];
    this.fuelLevel['50'] = (await this.getDataUri('assets/icon/gauge_half@2x.png', false))[0];
    this.fuelLevel['75'] = (await this.getDataUri('assets/icon/gauge_threequarters@2x.png', false))[0];
    this.fuelLevel['100'] = (await this.getDataUri('assets/icon/gauge_full@2x.png', false))[0];

    // Loading the company logo
    let logo = this.tchek.get('devicePtr').get('companyPtr').get('logo');
    if (!logo)
      logo = { _url: 'assets/images/your-logo.png' };
    this.companyLogo = (await this.getDataUri(logo._url, true))[0];
    // console.log('companylogo', this.companyLogo);
    // Loading the company location map
    const map = 'assets/images/tchek_map.png';
    this.companyMap = (await this.getDataUri(map, true))[0];

    // Loading all other PDF values
    this.security = {
      title: 'Sécurité', values: [
        { title: 'Date et heure locale', desc: new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long', hour: '2-digit', minute: '2-digit' }) }, //"Lundi 14 janvier 2019 11h47"
        { title: 'Géolocalisation', desc: this.tchek.get('devicePtr').get('address') }, //"19 avenue Paul Héroult, 13014 Marseille"
        {
          title: 'Horodatage',
          desc: this.tchek.get('tchekCreatedAt').toLocaleDateString('fr-FR', { year: 'numeric', month: 'numeric', day: 'numeric' }) + ' ' + this.tchek.get('tchekCreatedAt').toLocaleTimeString('fr-FR') // TODO: Replace locale here with global config variable
        },
        { title: 'Coordonnées GPS', desc: '43.3413999,5.3600575' },
      ]
    };
    this.customer = {
      title: 'Locataire', values: [
        { title: 'Nom conducteur', desc: this.contract ? this.contract.get('conducteur') : '-' },
        { title: 'Email', desc: this.contract ? this.contract.get('email') : '-' },
        { title: 'Téléphone', desc: '' }
      ]
    };
    this.employee = {
      title: 'Agent', values: [
        { title: 'Agence', desc: 'Marseille' },
        { title: 'Nom agent', desc: 'Paul Le Roux' },
      ]
    };

    const _categorie = parse.Object.extend('Categorie');
    const query = new parse.Query(_categorie);
    query.equalTo('cle', this.tchek.get('vehiclePtr').get('categorie'));
    const cat = await query.first();
    this.vehicle = {
      title: 'Véhicule', values: [
        { title: 'Immatriculation', desc: this.tchek.get('vehiclePtr').get('immat') },
        { title: 'Marque', desc: this.tchek.get('vehiclePtr').get('brand') },
        { title: 'Modèle', desc: this.tchek.get('vehiclePtr').get('model') },
        { title: 'Type Cat.', desc: cat ? cat.get('code') : '-' }
      ]
    };

    switch (type) {
      case PdfType.STATE:
        // Merging checklist of contract to definitions in DB => this.checklist
        const _checklist = parse.Object.extend('CheckList');
        const query = new parse.Query(_checklist);
        const checklistDefinition = await query.limit(1000).find();
        // console.log('pdfData', pdfData);
        // console.log('checklistDef', checklistDefinition);
        this.checklist = [];
        for (const item of pdfData.Checklist as HElementChecklist[]) {
          for (const def of checklistDefinition) {
            // console.log('checking ', item.Cle, def.get('cle'));
            if (item.Cle === def.get('cle')) {
              // console.log('matched item', item);
              const checklistItem = new ChecklistItem();
              // Setting interesting values of the definition here
              checklistItem.cle = def.get('cle');
              checklistItem.libelle = def.get('libelle');
              checklistItem.obligatoire = def.get('obligatoire');
              checklistItem.code = def.get('code');
              checklistItem.reponse = item.Reponse;
              this.checklist.push(checklistItem);
            }
          }
        }
        // console.log('Generated checklist', this.checklist);
        return await this.generateStatePDF(pdfData);
      case PdfType.COMPARISON:
        break;
      case PdfType.UNIQUE:
      default:
        const imagesMatrix = pdfData;
        const imagesData = [];
        for (let i = 0; i < imagesMatrix.length; i++) {
          // console.log('imageMatrix', i, imagesMatrix[i]);
          if (imagesMatrix[i].comparePtr)
            imagesData.push(await this.getDataUriForTwo(
              imagesMatrix[i].path, imagesMatrix[i].path, true, true, imagesMatrix[i].cameraName, imagesMatrix[i].comparePtr.cameraName, imagesMatrix[i].pictureNumber, imagesMatrix[i].comparePtr.pictureNumber, imagesMatrix[i].filters, imagesMatrix[i].comparePtr.filters
            ));
          else
            imagesData.push(await this.getDataUri(
              imagesMatrix[i].path, true, imagesMatrix[i].cameraName, imagesMatrix[i].pictureNumber, imagesMatrix[i].filters
            ));
        }
        return await this.generateUniquePDF(imagesData);
    }
  }

  tableau_data(value): any {
    return {
      widths: [100, '*'],
      body:
        this.addLine(value),

    };
  }

  addLine(value): Array<any> {
    const sectionArray = [];
    const totalROIs = 0;
    // Add ROI text description
    for (let i = 0; i < value.values.length; i++) {
      sectionArray.push([{ text: value.values[i].title, color: '#8B8B8B', bold: true }, { text: value.values[i].desc, alignment: 'left' }]);
    }

    return sectionArray; //[[{ text: 'Date et heure locale', color: "#8B8B8B", bold: true }, { text: 'Lundi 14 janvier  2019 11h47', alignment: 'left' }],[{ text: 'Date et heure locale', color: "#8B8B8B", bold: true }, { text: 'Lundi 14 janvier  2019 11h47', alignment: 'left' }]];
  }

  addMainBox(value: { title: string; }): any {
    return {
      table: {
        widths: ['*'],
        body: [
          [{ text: value.title }],
          [{ table: this.tableau_data(value), layout: 'noBorders' }]
        ]
      }, layout: 'noBorders', margin: [7, 0, 7, 0]
    };
  }

  addMapCell(): any {
    return {
      widths: ['*', 'auto'],
      layout: 'noBorders',
      body: [
        [
          this.addMainBox(this.security),
          {
            layout: 'whiteBorders',
            table: {
              widths: ['auto'],
              body: [[{
                image: this.companyMap.dataUri,
                width: '140',
                alignment: 'center'
              }]]
            }
          }
        ]
      ]
    };
  }
  addSectionVehicle(): any {
    return {
      margin: [0, 10, 0, 0],
      style: 'tableExample',
      fillColor: '#F4F4F4',
      layout: 'noBorders',
      table: {
        widths: ['*'],
        body: [
          [this.addMainBox(this.vehicle)],
        ]
      }
    };
  }
  addSection2Colums(): any {
    return {
      margin: [0, 10, 0, 0],
      style: 'tableExample',
      fillColor: '#F4F4F4',
      layout: 'noBorders',
      table: {
        widths: ['*', 1, '*'],
        body: [
          [this.addMainBox(this.customer), { text: '', fillColor: 'white' }, this.addMainBox(this.employee)],
        ]
      }
    };
  }
  addState(isReturn: boolean): any {
    let direction;
    let km;
    let fuel;
    if (isReturn) {
      direction = 'retour';
      km = 'kmRetour';
      fuel = 'niveauCarbRetour';
    } else {
      direction = 'depart';
      km = 'kmDepart';
      fuel = 'niveauCarbDepart';
    }

    return {
      margin: [10, 10, 10, 10],
      style: 'tableExample',
      layout: 'noBorders',
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: this.contract ? this.contract.get(direction).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long', hour: '2-digit', minute: '2-digit' }) : '-',
              bold: true
            }
          ],
          [
            {
              text: (this.contract ? this.contract.get(km) : '-') + ' km',
              bold: true
            }
          ],
          [
            {
              image: this.contract ? this.fuelLevel[
                (Math.round(this.contract.get(fuel) / 25) * 25).toString()
              ].dataUri : this.fuelLevel[0].dataUri,
              width: '120',
              alignment: 'center'
            }
          ]
        ]
      }
    };
  }
  addSectionState(): any {
    return [{
      margin: [0, 10, 0, 0],
      style: 'tableExample',
      fillColor: '#F4F4F4',
      layout: 'noBorders',
      table: {
        widths: [80, '*', 80],
        body: [
          [
            { text: 'DÉPART', style: 'grayCell' }, { text: '', fillColor: 'white' }, { text: 'RETOUR', style: 'grayCell' }],
        ]
      }
    }, {
      margin: [0, 0, 0, 0],
      style: 'tableExample',
      fillColor: '#F4F4F4',
      layout: 'noBorders',
      table: {
        widths: ['*', 1, '*'],
        body: [
          [this.addState(false), { text: '', fillColor: 'white' }, this.addState(true)],
        ]
      },
      pageBreak: 'after'
    }];
  }
  titleSection(): any {
    return [
      {
        columns: [
          {
            stack: [
              { text: 'Rapport d’état - INITIAL', /*+'ID N° JFEWIFJO' */ style: 'title' },
              {
                text: 'Location AGENCE - ETAT DES LIEUX' + (this.tchek.get('devicePtr').get('companyPtr').get('name') ? ' - ' + this.tchek.get('devicePtr').get('companyPtr').get('name') : ''),
                style: 'subtitle'
              }
            ],
            width: '*'
          },
          {
            image: this.companyLogo.dataUri,
            width: '80'
          }
        ],
        margin: [0, 0, 0, 60]
      },
      {
        style: 'tableExample',
        table: {
          widths: ['*', 150],
          body: [
            ['', { text: 'RÉFÉRENCE : fcntw44SEn', style: 'greenCell', bold: true }],
            [{

              table: this.addMapCell(),
              fillColor: '#F4F4F4', colSpan: 2, layout: 'noBorders', margin: [0, 0, 7, 7]
            }
            ]
          ]
        }, layout: 'noBorders',

      }];
  }

  generateUniquePDF(imagesData: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const docDefinition = { // margin: [left, top, right, bottom]
        info: {
          title: `Rapport d'état - ${this.tchek.get('vehiclePtr').get('immat')} - ${this.getPdfDate()}`,
          author: 'Tchek',
          subject: `Rapport d'état de véhicule produit par un scanner Tchek.`,
          creator: 'Tchek',
          producer: 'Tchek'
        },
        header: [
          /* {
            image: this.headerImage.dataUri,
            width: 595,
          } */
        ],
        content: [
          this.titleSection(),
          this.addSection2Colums(),
          this.addSectionVehicle(),
          this.addSectionState(),

          /* {
            text: 'Merci pour votre confiance et à bientôt dans nos réseaux ' + this.tchek.get('vehiclePtr').get('companyPtr').get('name'),
            alignment: 'center'
          },
          this.infoSection(),
          {
            text: 'VEHICULE',
            style: 'title2'
          },
          this.vehicleSection(), */
          {
            text: 'ANNOTATIONS',
            style: 'title2'
          },
          this.roiSection(imagesData)
        ],
        footer: this.footerSection,
        // margin: [left, top, right, bottom] OR [horizontal, vertical] OR [all]
        styles: {
          header: {
            fontSize: 22,
            bold: true
          },
          tableExample: {
            margin: [0, 0, 0, 0]
          },
          greenCell: {
            fillColor: '#64C7C9', color: 'white', margin: [5, 5, 5, 5], alignment: 'center'
          },
          grayCell: {
            fillColor: '#414141', color: 'white', margin: [5, 5, 5, 5], alignment: 'center'
          },
          footer: {
            margin: 10,
            italic: true,
            alignment: 'left',
            fontSize: 10,
            color: '#808080'
          },
          title: {
            fontSize: 18,
            bold: true,
            alignment: 'left',
            margin: [0, 0, 0, 0]
          },
          subtitle: {
            fontSize: 16,
            bold: true,
            alignment: 'left',
            margin: [0, 0, 0, 0]
          },
          title2: {
            fontSize: 16,
            bold: true,
            alignment: 'center',
            margin: [0, 30, 0, 0]
          },
          pageNumber: {
            alignment: 'right',
            fontSize: 10
          },
          certified: {
            fontSize: 10,
            color: '#808080'
          },
          selectedPicture: {
            alignment: 'center'
          }
        },
        defaultStyle: {
          font: 'Quicksand'
        },
        pageSize: 'A4',
        pageOrientation: 'portrait',
        pageMargins: [40, 20, 40, 60],
      };
      // console.log('docDef', docDefinition); .download(docDefinition.info.title)
      resolve(pdfMake.createPdf(docDefinition));
    });
  }

  infoSection(): any {
    return {
      columns: [
        {
          text: [
            { text: this.tchek.get('vehiclePtr').get('companyPtr').get('name') + '\n', bold: true },
            this.tchek.get('vehiclePtr').get('companyPtr').get('address') + '\n',
          ],
          alignment: 'left'
        },
        { // TODO: Replace locale here with global config variable
          text: 'Le ' + new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'numeric', day: 'numeric' }),
          alignment: 'right',
          margin: [0, 20, 0, 0]
        }
      ],
      alignment: 'justify',
      margin: [0, 20, 0, 0]
    };
  }

  vehicleSection(): any {
    return {
      columns: [
        {
          text: [
            // { text: 'Nom du client\n', bold: true },
            { text: 'Immatriculation\n', bold: true },
            'Marque\n',
            'Modèle\n',
            /*'Carroserie\n',
            'Couleur\n',
            'Conditions\n',
            { text: 'Kilométrage\n', bold: true },
            'Energie\n',
            'Boîte de vitesse\n'*/
            'Date de passage\n'
          ],
          alignment: 'left',
          margin: [60, 0, 0, 0],
          width: 'auto'
        },
        {
          text: [
            // { text: 'John Doe\n', bold: true },
            { text: this.tchek.get('vehiclePtr').get('immat') + '\n', bold: true },
            this.tchek.get('vehiclePtr').get('brand') + '\n',
            this.tchek.get('vehiclePtr').get('model') + '\n',
            /*'Berline\n',
            'Pourpre\n',
            'Au garage\n',
            { text: '1565089 km\n', bold: true },
            'Essence\n',
            'Manuelle\n'*/
            // TODO: Replace locale here with global config variable
            this.tchek.get('tchekCreatedAt').toLocaleDateString('fr-FR', { year: 'numeric', month: 'numeric', day: 'numeric' }) +
            ' ' + this.tchek.get('tchekCreatedAt').toLocaleTimeString('fr-FR')
          ],
          alignment: 'center',
          width: '*'
        }
      ],
      margin: [0, 20, 0, 0],
      pageBreak: 'after'
    };
  }

  roiSection(imagesData: Array<any>): Array<any> {
    const sectionArray = []; let totalROIs = 0;
    // Add ROI text description
    for (let i = 0; i < imagesData.length; i++) {
      // console.log('image data : ', imagesData[i]);
      if (imagesData[i].length == 1) { // Regular fav
        if (imagesData[i][0].rois.length > 0) {
          for (let j = 0; j < imagesData[i][0].rois.length; j++) {
            sectionArray.push({
              columns: [
                {
                  image: this.arrowDownImage.dataUri,
                  width: 8, margin: [0, 3, 0, 0]
                },
                { text: 'Annotation ' + (++totalROIs), width: 'auto', bold: true },
                { text: imagesData[i][0].rois[j].get('type'), width: 'auto' },
                { text: imagesData[i][0].rois[j].get('location'), width: 'auto' },
                // { text: 'JJ/MM/AAAA HH:mm', width: 'auto' },
                { text: imagesData[i][0].rois[j].get('comment'), width: '*' }
              ],
              columnGap: 10,
              margin: [0, 2, 0, 2]
            });
          }
        } else {
          sectionArray.push({
            columns: [
              {
                image: this.arrowDownImage.dataUri,
                width: 8, margin: [0, 3, 0, 0]
              },
              {
                text: 'Image selectionnée sans annotation ',
                width: 'auto', bold: true
              }
            ],
            columnGap: 10,
            margin: [0, 2, 0, 2]
          });
        }

        // Add the ROI image
        if (i === imagesData.length - 1) // Don't add a page break on the last ROI
          sectionArray.push({
            image: imagesData[i][0].dataUri,
            width: 560,
            margin: [-0, 4],
            style: 'selectedPicture'
          });
        else
          sectionArray.push({
            image: imagesData[i][0].dataUri,
            width: 560,
            margin: [-0, 0],
            pageBreak: 'after',
            style: 'selectedPicture'
          });
      } else { // Comparison fav
        sectionArray.push({
          columns: [
            {
              image: this.arrowDownImage.dataUri,
              width: 8, margin: [0, 3, 0, 0]
            },
            {
              text: 'Comparaison sur deux Tcheks distincts',
              width: 'auto', bold: true
            }
          ],
          columnGap: 10,
          margin: [0, 2, 0, 2]
        });

        // Add the ROI image
        if (i === imagesData.length - 1) // Don't add a page break on the last ROI
          sectionArray.push({
            image: imagesData[i][0].dataUri,
            width: 540,
            margin: [-0, 4],
            style: 'selectedPicture'
          }, {
            image: imagesData[i][1].dataUri,
            width: 540,
            margin: [-0, 4],
            style: 'selectedPicture'
          });
        else if (i === 0) // Smaller images on first to account for title
          sectionArray.push({
            image: imagesData[i][0].dataUri,
            width: 500,
            margin: [-0, 4],
            style: 'selectedPicture'
          }, {
            image: imagesData[i][1].dataUri,
            width: 500,
            margin: [-0, 4],
            style: 'selectedPicture'
          });
        else
          sectionArray.push({
            image: imagesData[i][0].dataUri,
            width: 540,
            margin: [-0, 4],
            style: 'selectedPicture'
          }, {
            image: imagesData[i][1].dataUri,
            width: 540,
            margin: [-0, 0],
            pageBreak: 'after',
            style: 'selectedPicture'
          });
      }
    }
    return sectionArray;
  }

  footerSection(pageNumber, pageCount, bar): Array<any> {
    return [
      /* {
        image: bar.dataUri
      }, */
      {
        text: 'Certifié par Tchek',
        width: '*',
        margin: [10, 0],
        style: 'certified'
      },
      {
        columns: [
          {
            text: 'TCHEK SAS – 19 Paul Héroult 13015 Marseille - Siret 82417039300022\n' +
              'contact@tchek.fr - www.tchek.fr - 07 68 66 54 05',
            width: '*',
            style: 'footer'
          },
          {
            text: `${pageNumber.toString()}\\${pageCount}`,
            width: '64',
            style: ['footer', 'pageNumber']
          }
        ],
        columnGap: 10
      }
    ];
  }

  getPdfDate(): string {
    const today = new Date();
    let pdfDate: string;

    // Adding day
    if (today.getDate() < 10)
      pdfDate = `0${today.getDate()}`;
    else
      pdfDate = today.getDate().toString();
    // Adding month
    if (today.getMonth() < 9)
      pdfDate += `0${today.getMonth() + 1}`;
    else
      pdfDate += (today.getMonth() + 1).toString();
    // Adding year
    pdfDate += today.getFullYear().toString().slice(2);

    return pdfDate;
  }

  getDataUri(url: string, compress = true, roiFolder?: string, roiPicture?: number, filters?: FavoritePictureFilters): Promise<any> {
    /* image.crossOrigin = "anonymous";
    image.setAttribute("crossOrigin", "anonymous"); */
    const maxWidth = 1920, maxHeight = 1291;
    return new Promise((resolve, reject) => {
      fabric.textureSize = 3088 * 2076; // Setting max texture size to HiRes pictures resolution
      fabric.Image.fromURL(url, (myImg) => {
        myImg.crossOrigin = 'anonymous';
        // Draw the image and its ROIs and rasterize
        const canvas = new fabric.Canvas(document.createElement('canvas'));
        canvas.setWidth(maxWidth);
        canvas.setHeight(maxHeight);
        myImg.scaleToWidth(canvas.getWidth());
        if (filters !== undefined)
          myImg = this.applyImageFilters(myImg, filters);
        canvas.add(myImg);
        this.addTchekDateToCanvas(canvas);
        if (roiFolder !== undefined && roiPicture !== undefined) {
          this.addRoisToCanvas(canvas, roiFolder, roiPicture).then(roisAdded => {
            if (compress) {
              resolve([{
                dataUri: canvas.toDataURL({ format: 'jpeg', quality: 0.92 }),
                rois: roisAdded
              }]);
            } else
              resolve([{
                dataUri: canvas.toDataURL({ format: 'png' }),
                rois: roisAdded
              }]);
          });
        } else { // Get the image dataURL without drawing
          if (compress)
            resolve([{ dataUri: myImg.toDataURL({ format: 'jpeg', quality: 0.92 }) }]);
          else
            resolve([{ dataUri: myImg.toDataURL({ format: 'png' }) }]);
        }
      }, { crossOrigin: 'anonymous' });
    });
  }

  async getDataUriForTwo(url: string, url2: string, compress = true, compress2 = true, roiFolder?: string, roiFolder2?: string, roiPicture?: number, roiPicture2?: number, filters?: FavoritePictureFilters, filters2?: FavoritePictureFilters): Promise<any> {
    /* image.crossOrigin = "anonymous";
    image.setAttribute("crossOrigin", "anonymous"); */
    const result = []; // returned array
    const maxWidth = 1920, maxHeight = 1291;
    return new Promise((resolve, reject) => {
      fabric.textureSize = 3088 * 2076; // Setting max texture size to HiRes pictures resolution
      fabric.Image.fromURL(url, (myImg) => {
        myImg.crossOrigin = 'anonymous';
        // Draw the image and its ROIs and rasterize
        const canvas = new fabric.Canvas(document.createElement('canvas'));
        canvas.setWidth(maxWidth);
        canvas.setHeight(maxHeight);
        myImg.scaleToWidth(canvas.getWidth());
        if (filters !== undefined)
          myImg = this.applyImageFilters(myImg, filters);
        canvas.add(myImg);
        this.addTchekDateToCanvas(canvas);
        if (roiFolder !== undefined && roiPicture !== undefined) {
          this.addRoisToCanvas(canvas, roiFolder, roiPicture).then(roisAdded => {
            if (compress) {
              result.push({
                dataUri: canvas.toDataURL({ format: 'jpeg', quality: 0.92 }),
                rois: roisAdded
              });
            } else
              result.push({
                dataUri: canvas.toDataURL({ format: 'png' }),
                rois: roisAdded
              });
            // STEP 2
            fabric.Image.fromURL(url2, (myImg) => {
              myImg.crossOrigin = 'anonymous';
              // Draw the image and its ROIs and rasterize
              canvas.setWidth(maxWidth);
              canvas.setHeight(maxHeight);
              myImg.scaleToWidth(canvas.getWidth());
              if (filters2 !== undefined)
                myImg = this.applyImageFilters(myImg, filters2);
              canvas.add(myImg);
              this.addTchekDateToCanvas(canvas);
              if (roiFolder2 !== undefined && roiPicture2 !== undefined) {
                this.addRoisToCanvas(canvas, roiFolder2, roiPicture2).then(roisAdded => {
                  if (compress2) {
                    result.push({
                      dataUri: canvas.toDataURL({ format: 'jpeg', quality: 0.92 }),
                      rois: roisAdded
                    });
                  } else
                    result.push({
                      dataUri: canvas.toDataURL({ format: 'png' }),
                      rois: roisAdded
                    });
                  resolve(result);
                });
              } else { // Get the image dataURL without drawing
                if (compress2)
                  result.push({ dataUri: myImg.toDataURL({ format: 'jpeg', quality: 0.92 }) });
                else
                  result.push({ dataUri: myImg.toDataURL({ format: 'png' }) });
                resolve(result);
              }
            }, { crossOrigin: 'anonymous' });
          });
        } else { // Get the image dataURL without drawing
          if (compress)
            result.push({ dataUri: myImg.toDataURL({ format: 'jpeg', quality: 0.92 }) });
          else
            result.push({ dataUri: myImg.toDataURL({ format: 'png' }) });

          // STEP 2
          fabric.Image.fromURL(url2, (myImg) => {
            myImg.crossOrigin = 'anonymous';
            // Draw the image and its ROIs and rasterize
            canvas.setWidth(maxWidth);
            canvas.setHeight(maxHeight);
            myImg.scaleToWidth(canvas.getWidth());
            if (filters2 !== undefined)
              myImg = this.applyImageFilters(myImg, filters2);
            canvas.add(myImg);
            this.addTchekDateToCanvas(canvas);
            if (roiFolder2 !== undefined && roiPicture2 !== undefined) {
              this.addRoisToCanvas(canvas, roiFolder2, roiPicture2).then(roisAdded => {
                if (compress2) {
                  result.push({
                    dataUri: canvas.toDataURL({ format: 'jpeg', quality: 0.92 }),
                    rois: roisAdded
                  });
                } else
                  result.push({
                    dataUri: canvas.toDataURL({ format: 'png' }),
                    rois: roisAdded
                  });
                resolve(result);
              });
            } else { // Get the image dataURL without drawing
              if (compress2)
                result.push({ dataUri: myImg.toDataURL({ format: 'jpeg', quality: 0.92 }) });
              else
                result.push({ dataUri: myImg.toDataURL({ format: 'png' }) });
              resolve(result);
            }
          }, { crossOrigin: 'anonymous' });
        }
      }, { crossOrigin: 'anonymous' });
    });
  }

  // Add ROIs to canvas
  addRoisToCanvas(canvasRef, folder, numberPicture): Promise<any> {
    return new Promise((resolve, reject) => {
      this._roiService.getPictureRois(this.tchek.id, folder, numberPicture)
        .then((retrievedRois) => {
          // console.log('rois for ', this.tchek.id, folder, numberPicture, ':', retrievedRois);
          // Adding ROIs to canvas if any
          if (retrievedRois.length > 0) {
            let roiCircle;
            let roiText;
            for (let i = 0; i < retrievedRois.length; i++) {
              roiCircle = new fabric.Circle({
                id: i,
                left: 0,
                top: 0,
                fill: 'transparent',
                radius:
                  retrievedRois[i].get('radius') * canvasRef.getWidth(),
                strokeWidth: 5,
                stroke: retrievedRois[i].get('isAuto') ? 'white' : 'red',
                selectable: false,
                originX: 'center',
                originY: 'center',
                draggable: false,
                lockRotation: true,
                hasControls: false
              });
              roiText = new fabric.Text(
                retrievedRois[i].get('type'), {
                fontSize: 28,
                fontFamily: 'Open Sans',
                fill: 'white',
                fontWeight: 'bold',
                originX: 'center',
                originY: 'bottom',
                draggable: false,
                selectable: false,
                lockRotation: true,
                hasControls: false,
                left: 0,
                top: - retrievedRois[i].get('radius') * canvasRef.getWidth() -
                  16,
                //shadow: 'rgba(0,0,0,0.6) 2px 2px 1px'
              });
              const roiTextBox = new fabric.Rect({
                width: roiText.getScaledWidth() + 32 + 16,
                height: roiText.getScaledHeight() + 8,
                cornerStyle: 'circle',
                strokeLineJoin: 'round',
                strokeWidth: 10,
                stroke: 'rgb(105,183,182)',
                fill: 'rgb(105,183,182)',
                originX: 'center',
                originY: 'bottom',
                left: roiText.getCenterPoint().x,
                top: roiText.top + 8
              });
              const roiTextNotch = new fabric.Triangle({
                width: 12,
                height: 6,
                originX: 'center',
                originY: 'bottom',
                fill: 'rgb(105,183,182)',
                left: roiTextBox.left,
                top: roiTextBox.top - 1,
                angle: 180
              });
              canvasRef.add(new fabric.Group([roiTextNotch, roiCircle, roiTextBox, roiText], {
                left:
                  (retrievedRois[i].get('centerX') * canvasRef.getWidth()),
                top:
                  (retrievedRois[i].get('centerY') *
                    canvasRef.getHeight() + roiTextBox.top / 2),
                originX: 'center',
                originY: 'center',
                lockRotation: true,
                hasControls: false,
                selectable: false,
                evented: false,
                parseId: retrievedRois[i].id,
                isAuto: retrievedRois[i].get('isAuto'),
                isValidated: retrievedRois[i].get('isValidated'),
                opacity: retrievedRois[i].get('isAuto') && retrievedRois[i].get('isValidated') ||
                  !retrievedRois[i].get('isAuto') ? 1 : 0.3
              }));
            }
            resolve(retrievedRois);
          } else {
            resolve(retrievedRois);
          }
        }, (error) => {
          reject(error);
        });
    });
  }

  // Add date on the top right to canvas
  addTchekDateToCanvas = function (canvasRef) {
    const tchekDate = this.tchek.get('tchekCreatedAt').toLocaleDateString('fr-FR', { year: 'numeric', month: 'numeric', day: 'numeric' }) +
      ' ' + this.tchek.get('tchekCreatedAt').toLocaleTimeString('fr-FR');
    const dateText = new fabric.Text(
      tchekDate, {
      fontSize: 42,
      fontFamily: 'Sans Serif',
      fill: 'rgba(255,255,255,0.33)',
      fontWeight: 'bold',
      originX: 'right',
      originY: 'top',
      draggable: false,
      selectable: false,
      lockRotation: true,
      hasControls: false,
      left: 1920 - 64, // max 3088
      top: 60,
      shadow: 'rgba(0,0,0,0.6) 2px 2px 1px'
    });
    canvasRef.add(dateText);
  };

  applyImageFilters(imgRef: any, filters: FavoritePictureFilters): any {
    // console.log('Applying filters ', filters);
    if (filters.gamma)
      imgRef.filters.push(new fabric.Image.filters.Gamma({ gamma: [filters.gamma, filters.gamma, filters.gamma] }));
    if (filters.contrast)
      imgRef.filters.push(new fabric.Image.filters.Contrast({ contrast: filters.contrast }));
    if (filters.saturation)
      imgRef.filters.push(new fabric.Image.filters.Saturation({ saturation: filters.saturation }));
    if (filters.inversion)
      imgRef.filters.push(new fabric.Image.filters.Invert());
    imgRef.applyFilters();
    return imgRef;
  }

  // Returns the type, comment, location, date for the picture in an array
  async getPDFRoisDetail(roiFolder, roiPicture): Promise<Array<any>> {
    const pictureRois = await this._roiService.getPictureRois(this.tchek.id, roiFolder, roiPicture);
    const roiDetails = [];
    for (let i = 0; i < pictureRois.length; i++) {
      if (pictureRois[i].get('folder') === roiFolder && pictureRois[i].get('picture') === roiPicture) {
        roiDetails.push({
          type: pictureRois[i].get('type'),
          location: pictureRois[i].get('location'),
          comment: pictureRois[i].get('comment'),
          createdAt: pictureRois[i].createdAt,
          updatedAt: pictureRois[i].updatedAt
        });
      }
    }
    return roiDetails;
  }

  stateHeader(stateData: ContractUpdate): any {
    return [{
      columns: [
        {
          width: '50%',
          stack: [
            {
              image: this.companyLogo.dataUri,
              width: '80'
            },
            { text: '\nNom Agence', bold: true },
            { text: '99, Adresse ligne 1' },
            { text: 'Adresse ligne 2' },
            { text: '13000 MARSEILLE' },
            { text: 'TEL/FAX' },
            { text: 'www.exemple.com' },
            { text: 'contact@exemple.com' },
            { text: 'N° SIREN 99999999999' },
          ]
        },
        {
          width: '50%',
          stack: [
            { text: 'LOCATAIRE', alignment: 'center', bold: true },
            { text: 'Prénom Nom' },
            { text: 'Société' },
            { text: '99, Adresse ligne 1' },
            { text: 'Adresse ligne 2' },
            { text: '13000 MARSEILLE' },
            { text: `\nETAT ${stateData.Type === 'D' ? 'DEPART' : 'RETOUR'}`, bold: true },
            {
              text: [
                { text: 'Contrat de location ', bold: true },
                `n° ${stateData.Contrat}`
              ]
            },
            {
              columns: [
                {
                  width: '50%',
                  text: [
                    { text: 'IMMAT. ', bold: true },
                    `${this.tchek.get('vehiclePtr').get('immat')}`
                  ]
                },
                {
                  width: '50%',
                  text: [
                    { text: 'CAT. ', bold: true },
                    `9`
                  ]
                }
              ]
            },
            {
              text: [
                { text: 'VEHICULE :', bold: true },
                `STRING`
              ]
            }
          ]
        }
      ],
      columnGap: 10
    },
    {
      columns: [
        {
          width: '33%',
          text: [
            { text: 'DEPART : ', bold: true },
            ``
          ]
        }, {
          width: '33%',
        }, {
          width: '33%',
        }
      ]
    }];
  }

  addChecklist() {
    // console.log('adding checklist to pdf', this.checklist);
    const valuesArray = this.checklist.map(value => {
      let response = '';
      switch (value.reponse) {
        case 'O':
          response = 'Oui';
          break;
        case 'N':
          response = 'Non';
          break;
        default:
          response = value.reponse;
      }
      return {
        text: [
          { text: `${value.libelle} (${value.code}) : `, bold: true, color: '#8B8B8B' },
          response
        ], margin: [7, 0, 0, 0]
      };
    });
    // console.log('values', valuesArray);
    const valuesStructured = [];
    for (let i = 0; i < valuesArray.length; i++) {
      const row = [];
      if (valuesArray[i + (valuesArray.length / 2)])
        valuesStructured.push([
          valuesArray[i],
          valuesArray[i + (valuesArray.length / 2)]
        ]);
      else
        valuesStructured.push([valuesArray[i], '']);
    }
    // console.log('structured', valuesStructured);
    return {
      style: 'tableExample',
      fillColor: '#F4F4F4',
      layout: 'noBorders',
      table: {
        widths: ['*', '*'],
        body: [
          [{ text: 'Equipement', margin: [7, 0, 0, 7] }, ''],
          ...valuesStructured
        ]
      }
    };
  }

  async generateStatePDF(stateData: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const docDefinition = { // margin: [left, top, right, bottom]
        info: {
          title: `Rapport d'état - ${this.tchek.get('vehiclePtr').get('immat')} - ${this.getPdfDate()}`,
          author: 'Tchek',
          subject: `Rapport d'état de véhicule produit par un scanner Tchek.`,
          creator: 'Tchek',
          producer: 'Tchek'
        },
        header: [
          /* {
            image: this.headerImage.dataUri,
            width: 595,
          } */
        ],
        content: [
          this.titleSection(),
          this.addSection2Colums(),
          this.addSectionVehicle(),
          this.addSectionState(),
          this.addChecklist()
          /* {
            text: 'Merci pour votre confiance et à bientôt dans nos réseaux ' + this.tchek.get('vehiclePtr').get('companyPtr').get('name'),
            alignment: 'center'
          },
          this.infoSection(),
          {
            text: 'VEHICULE',
            style: 'title2'
          },
          this.vehicleSection(), */
        ],
        footer: this.footerSection,
        // margin: [left, top, right, bottom] OR [horizontal, vertical] OR [all]
        styles: {
          header: {
            fontSize: 22,
            bold: true
          },
          tableExample: {
            margin: [0, 0, 0, 0]
          },
          greenCell: {
            fillColor: '#64C7C9', color: 'white', margin: [5, 5, 5, 5], alignment: 'center'
          },
          grayCell: {
            fillColor: '#414141', color: 'white', margin: [5, 5, 5, 5], alignment: 'center'
          },
          footer: {
            margin: 10,
            italic: true,
            alignment: 'left',
            fontSize: 10,
            color: '#808080'
          },
          title: {
            fontSize: 18,
            bold: true,
            alignment: 'left',
            margin: [0, 0, 0, 0]
          },
          subtitle: {
            fontSize: 16,
            bold: true,
            alignment: 'left',
            margin: [0, 0, 0, 0]
          },
          title2: {
            fontSize: 16,
            bold: true,
            alignment: 'center',
            margin: [0, 30, 0, 0]
          },
          pageNumber: {
            alignment: 'right',
            fontSize: 10
          },
          certified: {
            fontSize: 10,
            color: '#808080'
          },
          selectedPicture: {
            alignment: 'center'
          }
        },
        defaultStyle: {
          font: 'Quicksand'
        },
        pageSize: 'A4',
        pageOrientation: 'portrait',
        pageMargins: [40, 20, 40, 60],
      };
      // console.log('docDef', docDefinition); .download(docDefinition.info.title)
      resolve(pdfMake.createPdf(docDefinition));
    });
  }

  async requestDamagePDF(tchekId: string, contractUpdate?: ContractUpdate) {
    return new Promise<any | any>((resolve, reject) => {
      parse.Cloud.run('pdfDamage', {
        tchekId,
        contractUpdate
      }).then((response: any) => {
        resolve(response);
      }, (error: any) => {
        reject(error);
      });
    });
  }

  async requestDamagePDFFromReport(reportId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      parse.Cloud.run('pdfDamageFromReport', {
        language: this.locale,
        reportId,
      }).then((response: any) => {
        resolve(response);
      }, (error: any) => {
        reject(error);
      });
    });
  }

  async downloadReport(reportId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      parse.Cloud.run('downloadPDF', {
        reportId
      }).then(response => resolve(response),
        error => reject(error));
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/service/pdf.service.ts